import React, { useRef, useState, useEffect } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import DropTargetNextToListItem from './DropTargetNextToListItem';
import { DraggedItem, DropResult, DroppedItemProps } from '../../interfaces/DnDInterfaces';
import CollapseToggle from '../layout/CollapseToggle';
import Spacer from '../layout/Spacer';
import StringProperty from '../properties/StringProperty';
import { IconFillStyles } from '../Iconset';
import useForceUpdate from '../reactUtils/useForceUpdate';
import { DecoratorMap } from '../../classes/DecoratorMap';

export default function ListItem(props)
{
	const forceUpdate = useForceUpdate();

	const rootRef = useRef<HTMLDivElement>(null);

	const oListDecorators = props.parentDecoratorsMapID ?
		Object.assign({}, props, DecoratorMap.getMapping(props.parentDecoratorsMapID)) //Merge
		: props;

	//getIndex; updateItemMap; selectionQuery;  expansionQuery;  contextCallback; dnd; onSelectCallback;
	// onToggleExpandedStateCallback; onDroppedCallback; getDraggedObjects; doubleClickCallback;
	// activateRename;	//Seems to have a private function by this name

	// const getMyIndex 					= ()=>oListDecorators.getIndex(props.nodeId);
	const getChildrenArray				= oListDecorators.getChildrenArray;
	const registerItemRefInList 		= oListDecorators.registerItemRefInList;
	const scrollToSelectedItem			= oListDecorators.scrollToSelectedItem; //passing the scroll to selected item function from the list view
	const autoScroll					= oListDecorators.autoScroll; //passing the autoscroll prop from the list view
	const onSelectCallback 				= oListDecorators.onSelectCallback;
	const onToggleExpandedStateCallback = oListDecorators.onToggleExpandedStateCallback;
	const onDroppedCallback 			= oListDecorators.onDroppedCallback;
	const getDraggedObjects 			= oListDecorators.getDraggedObjects;
	const useAltColor 					= oListDecorators.useAltColor;
	const doubleClickCallback = oListDecorators.doubleClickCallback;

	const isSelected = oListDecorators.selectionQuery !== undefined ? oListDecorators.selectionQuery(props.nodeId) : false;
	const autoExpand = oListDecorators.expansionQuery !== undefined ? oListDecorators.expansionQuery(props.nodeId) : false;
	const indentLevel = props.indentOverride !== undefined ? props.indentOverride : oListDecorators.getIndentLevel !== undefined ?
		oListDecorators.getIndentLevel(props.nodeId) :
		props.indentLevel !== undefined ?
			props.indentLevel :
			props.parent ? 1 : 0;
	const contextCallback = props.contextActionOverride !== undefined ? props.contextActionOverride : oListDecorators.contextCallback;
	const myState = props.state !== undefined ? props.state : isSelected ? "selected" : "none";
	const isSelectable = props.isSelectable !== undefined ? props.isSelectable : true;
	const dndSettings = { ...oListDecorators.dnd, ...props.dndOverrides };

	const [isRenaming, setIsRenaming] = useState(false);
	const [tempName, setTempName] = useState("");
	const [previousName, setPreviousName] = useState("");
	const [isExpanded, setIsReallyExpanded] = useState(oListDecorators.expansionQuery !== undefined ? oListDecorators.expansionQuery(props.nodeId) : false);

	const renameStatus = useRef(false); //Bool that shows if the selected item is currently being renamed or not for external use.. im sorry.

	const defaultIconFill = props.iconFillStyleDefault ?? IconFillStyles.default;
	const selectedIconFill = props.iconFillStyleSelected ?? IconFillStyles.dark;

	const propsAndRefs = {
		...props,
		htmlElementRef: rootRef.current,
		setIsExpanded: (force = false)=>{setIsReallyExpanded(force ? true : oListDecorators.expansionQuery(props.nodeId))}, //added a way to force expantion on demand since the oListDecorators is WAY too slow
		renameFunctions: {
			isRenaming : renameStatus,
			clearRename: (r)=>
			{
				setIsRenaming(false);
				renameStatus.current = false;
				if(r && r.current && r.current.focus)
				{
					r.current.focus();
				}
			},
			activateRename: activateRename
		}
	}

	useEffect(() =>
	{
		if (isRenaming)
		{
			setTempName(props.label);
			setPreviousName(props.label);
		}
	}, [isRenaming]);

	useEffect(() =>
	{
		setIsReallyExpanded(oListDecorators.expansionQuery(props.nodeId));
	}, [autoExpand]);

	useEffect(() =>
	{
		if (props.parentDecoratorsMapID)
		{
			const sID = props.parentDecoratorsMapID+"_ITEMREF_"+props.nodeId;
			if (isSelected)
			{
				DecoratorMap.setMapping(sID, propsAndRefs);
				if(scrollToSelectedItem && autoScroll)
				{
					scrollToSelectedItem(); //scroll to items when they're selected... usually for select items through revision editor or slide through sequence editor
				}
			}
			else
			{
				DecoratorMap.unsetMapping(sID);
			}
		}
	}, [isSelected]);

	useEffect(() =>
	{
		propsAndRefs.htmlElementRef = rootRef.current; //we need to make sure the refs are updated (its null here if not manually set)
		registerItemRefInList(props.nodeId, propsAndRefs);
		if(scrollToSelectedItem && autoScroll && isSelected)
		{ //scroll to items when it's first created (usually for new material/slide/items)
			scrollToSelectedItem();
		}
	}, [rootRef.current]);

	// drag-n-drop
	const [, drag] = useDrag({
		type: dndSettings.dragType,
		item: {
			// this is weird. should check if within selection first, otherwise replace the selection
			// actually, this needs to be handled in the parent list altogether...
			'selected': composeDraggedCollection(), //{ index: myIndex, props: props }
		},
	});

	const [{ hovered, canDrop }, drop] = useDrop<DraggedItem, void, DropResult>({
		accept: dndSettings.dropDefinitions.map(definition => definition.type),
		collect: monitor => ({
			hovered: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop(),
		}),
		drop: (item: DraggedItem, monitor: DropTargetMonitor<DraggedItem, void>) =>
		{
			//make sure the drop event wasn't already handled
			if (!monitor.didDrop())
			{
				const dropped = new Array<DroppedItemProps>();

				item.selected.forEach(sel =>
				{
					// make sure it wasn't dropped on self
					if (sel.index !== oListDecorators.getIndex(props.nodeId) && sel.props.parentIndex !== oListDecorators.getIndex(props.nodeId))
					{
						dropped.push(sel.props);
					}
				});

				if (!handleDrop(item) && onDroppedCallback)
				{
					item.type = monitor.getItemType() as string; // inject the type into the item for new reactDND version
					onDroppedCallback(item, props);
				}
			}
		}
	});

	function handleDrop(item: DraggedItem)
	{
		const dropCallbackMap = new Map();

		function getCallbackForType(type: string | null)
		{
			if (!type) return null;
			
			let callbackFunc = null;
			for (const definition of dndSettings.dropDefinitions)
			{
				if (definition.type === type)
				{
					callbackFunc = definition.callback;
					break;
				}
			}

			return callbackFunc;
		}

		for (const dropped of item.selected)
		{
			const callback = getCallbackForType(item.type);
			if (!callback)
				continue;

			if (!dropCallbackMap.has(callback))
				dropCallbackMap.set(callback, []);

			dropCallbackMap.get(callback).push(dropped.props);
		}

		dropCallbackMap.forEach((value, key) => { key(value, props) });

		return dropCallbackMap.size;
	}

	function activateRename()
	{
		// console.log('activate rename');
		// console.log(props);
		if(props.nameCommittedCallback && !props.protected)
		{
			setIsRenaming(true);
			renameStatus.current = true;
		}
	}

	function composeDraggedCollection()
	{
		const selection = getDraggedObjects? getDraggedObjects() : [];
		if (!selection.some(sel => sel.index === oListDecorators.getIndex(props.nodeId)))
		{
			selection.push({ index: oListDecorators.getIndex(props.nodeId), props: props });
		}

		//console.log(selection.length);
		return selection;
	}

	function renderIndents()
	{
		const indents = new Array<JSX.Element>();
		for (let i = 0; i < indentLevel; i++)
		{
			indents.push(
				<div className="indent" key={i} />
			);
		}

		return indents;
	}

	const content = (
		<div ref={rootRef}
			//className={"listItem" + (props.childOrderIndex !== undefined && 0===props.childOrderIndex ? " listItemWrapped" : "")}
			className={[
				"listItem",
				(props.childOrderIndex !== undefined && 0===props.childOrderIndex ? " listItemWrapped" : ""),
				props.className,
			].join(' ').trim()}
			data-name={"list-item-root-" + props.label}
			tooltip={props["tooltip"]}
			data-id={props.nodeId}
			onDoubleClick={(e) => { doubleClickCallback?.(props) }}
		>
			<div className={"listItemHeader" + (dndSettings.dropBeforeAfterEnabled ? " listItemHeaderWrapped" : "")}
				data-grayedout={props.grayedOut ? "true" : "false"}
				data-status={myState}
				data-hover={hovered && canDrop ? "true" : "false"}
				data-altcolor={useAltColor || props.forceAltColor ? "true" : "false"}
				style={{ backgroundColor: props.forceManualBackground ? props.forceManualBackground : null, height: props.heightOverride ? props.heightOverride : null, width: props.widthOverride ? props.widthOverride : null }}
				id={props.id}
				data-name={props.label}
				onMouseMove={(e)=>
				{
					if(props.onMouseMoveCallback)
						props.onMouseMoveCallback(e);
				}}
				onMouseLeave={(e)=>
				{
					if(props.onMouseMoveCallback)
						props.onMouseMoveCallback();
				}}
				onClick={(e) =>
				{
					e.preventDefault();
					e.stopPropagation();

					// TODO: this should probably some class somewhere that constructs itself from either 'e' or Input manager and knows how to handle nulls
					const modifierKeys = {
						ctrlKey: e.ctrlKey || e.metaKey,
						shiftKey: e.shiftKey,
						altKey: e.altKey,
					}

					if (isSelectable && onSelectCallback)
						onSelectCallback(oListDecorators.getIndex(props.nodeId), modifierKeys);
				}}
				onContextMenu={(e) =>
				{
					e.preventDefault();
					e.stopPropagation();

					if (!isSelected && isSelectable && onSelectCallback)
						onSelectCallback(oListDecorators.getIndex(props.nodeId));

					// TODO: multi-select?
					if (contextCallback)
						contextCallback(props, e);
				}}
			>
				{/* indent content according to depth in hierarchy */}
				{
					renderIndents()
				}

				{/* render expansion toggle if we have children */}
				{
					(!props.children || props.children.length === 0) ? <div className="indentSmall" key="0" /> :
						<CollapseToggle className={"indentSmall"}
							fillStyle={props.arrowFillStyle ? props.arrowFillStyle : isSelected ? selectedIconFill : defaultIconFill}
							data-name={"list-item-" + props.label + "-toggle"}
							isExpanded={isExpanded}
							state={myState}
							smallSized={true}
							onToggleCallback={(value, recursive) =>
							{
								onToggleExpandedStateCallback?.(props.nodeId, value, recursive);
								forceUpdate();
							}}
						/>
				}

				{/* render item icon if defined */}
				{
					!props.iconRenderer ? null :
						<div className="listItemIcon"
							data-name={"list-item-" + props.label + "-icon"}
						>
							{React.cloneElement(props.iconRenderer, { fillStyle: props.iconRenderer.props.fillStyle ? props.iconRenderer.props.fillStyle : isSelected ? selectedIconFill : IconFillStyles.default })}
						</div>
				}

				{/* render the text of the item */}
				{
					isRenaming ?
						<StringProperty
							value={tempName}
							autoFocus
							style={{ maxHeight:'16px' }}
							onChangeCallback={(newTempName) =>
							{
								setTempName(newTempName);
								props.nameChangedCallback?.(newTempName);
							}}
							onCommitCallback={(newName) =>
							{
								setIsRenaming(false);
								renameStatus.current = false;
								if (tempName !== previousName)
								{
									props.nameCommittedCallback?.(newName);
								}
								if(rootRef?.current?.parentElement)
								{
									rootRef.current.parentElement.focus();
								}
							}}
						/>
						:
						<div className="listItemLabel"
							data-name={"list-item-" + props.label + "-label"}
							style={{width: props.labelWidthOverride ? props.labelWidthOverride : null}}
							onDoubleClick={(e) =>
							{
								if(!props.blockDoubleClickRename)
								{
									e.stopPropagation();
									activateRename();
								}
							}}
						>
							{
								// myIndex + "_" + props.label
								props.label
							}
						</div>
				}

				{/* render additional react components such as buttons etc. */}
				{
					!props.decorators ? null :
						<React.Fragment>
							<Spacer stretchTo={{ x: "100%" }} />
							<div className={"listItemDecoratorsContainer"}
								data-status={myState}
								data-name={"list-item-" + props.label + "-decorators-container"}
							>
								{
									Array.isArray(props.decorators) ?
										props.decorators.map((d, idx) =>
										{
											return (
												<React.Fragment key={idx}>
													{d}
													<Spacer size={{ x: "4px" }} />
												</React.Fragment>
											);
										})
										:
										props.decorators
								}
							</div>
						</React.Fragment>
				}
			</div>

			{/* continue hierarchy rendering if we're expanded */}
			{
				!isExpanded ? null :
					<div className="itemListChildrenContainer"
						data-name={"item-list-children-container"}
					>
						{
							props.children?
								props.parentDecoratorsMapID?
									getChildrenArray(props.children) :
								// React.Children.map(props.children, (child) =>
									getChildrenArray(props.children).map((child) =>
									{
										if (child)
											return React.cloneElement(child, {
												parentIndex: oListDecorators.getIndex(props.nodeId),
												indentLevel: (indentLevel + 1),
												propsDecorator: props.propsDecorator,
												...props.propsDecorator(),
											});
									})
								: null
						}
					</div>
			}
		</div>
	);

	let wrappedContent: React.ReactNode | null = null;
	if (isSelectable && dndSettings.dragEnabled)
	{
		if (dndSettings.dropEnabled)
		{
			wrappedContent = drag(drop(content));
		}
		else
		{
			wrappedContent = drag(content);
		}
	}
	else if (dndSettings.dropEnabled)
	{
		wrappedContent = drop(content);
	}
	else
	{
		wrappedContent = (content);
	}

	if (dndSettings.dropBeforeAfterEnabled)
	{
		return (
			<div>
				{
					props.childOrderIndex !== undefined && 0===props.childOrderIndex && dndSettings ?
						<DropTargetNextToListItem
							beforeItem={true}
							dndSettings={dndSettings}
							targetItemProps={props}
							handleDrop={handleDrop}
							onDroppedCallback={onDroppedCallback}
						/>

						: null
				}

				{wrappedContent}
				 {dndSettings ?
					<DropTargetNextToListItem
						beforeItem={false}
						dndSettings={dndSettings}
						targetItemProps={props}
						handleDrop={handleDrop}
						onDroppedCallback={onDroppedCallback}
					/>

					: null
				 }
			</div>
		);
	}

	return wrappedContent;
}
